/**
 * Modello della superficie **Statistiche SLA** (piano Client sez. 5 F4a, sez. 6
 * riga 90, sez. 8 riga 128).
 *
 * Cosa la superficie rende, e da dove viene (contratto **reale**, letto in sola
 * lettura dal backend Python P2 — non dedotto):
 * - **due basi separate** `original` ed `effective`, ognuna con `met`/`eligible`
 *   e la percentuale (`SlaComplianceResponse`, `slaModel.ts`); le due basi non
 *   sono intercambiabili e la superficie non ne calcola una dall'altra;
 * - **denominatore esplicito**: `eligible` e' il denominatore della compliance
 *   (`services/sla/statistics.py:206-209`, `eligible_completions`), reso con la
 *   sua etichetta accanto alla percentuale;
 * - **annullati/interrotti separati**: `excluded.cancelled`,
 *   `excluded.interrupted` e `excluded.authority_terminated` sono i
 *   `completion_kind` 2/3/4 (`services/sla/statistics.py:78-82`) e stanno
 *   **fuori** dal denominatore (`queries.py:55-73`): solo
 *   `COMPLETION_COMPLETED = 1` entra in `eligible`. Sono resi in un blocco
 *   distinto, mai sommati al denominatore;
 * - **senza SLA separato**: `without_sla` sono i risultati `NOT eligible`
 *   (`queries.py:71`), fuori dal denominatore e esposti a parte con la copertura
 *   (`coverage_percent`, `statistics.py:221-228`);
 * - **N/D con zero campione**: `percent`, `coverage_percent` e
 *   `average_working_seconds` sono `null` quando il denominatore (o il totale) e'
 *   zero (`statistics.py:192-199,221-235`). La superficie rende la chiave "non
 *   disponibile", **mai** uno `0%`;
 * - **periodo/timezone**: il periodo e' `[from, to)` a mezzanotte **locale**
 *   (`statistics.py:152-171`) e la risposta dichiara `period` e `timezone`: la
 *   superficie rende quelli del payload, non il fuso del browser;
 * - **nessuna metrica per persona**: le righe del drill-down non portano utente
 *   o ruolo (`statistics.py:334-339`: "Nessuna metrica nominativa di
 *   performance") e il livello raggiunto di escalation **non e' una metrica di
 *   P2 per decisione 8** (`statistics.py:26-55`). F4a non rende alcun
 *   utente/ruolo: `SLA_STATISTICS_DIMENSION` lo dichiara e il test verifica che
 *   il DTO non porti campi per persona.
 *
 * Cosa **F4b** aggiunge (sezione "F4b: trend, drill-down, campione e Cliente"
 * qui sotto), sempre sul contratto reale:
 * - **trend** (`GET /sla/{org}/trend`): punti per giorno/settimana locale, ognuno
 *   con **entrambe** le basi e gli stessi conteggi della conformita' (`bucket` =
 *   identita' stabile del punto);
 * - **drill-down** (`GET /sla/{org}/drilldown/process|step`): righe paginate con
 *   identita' `sla_instance_id` (`sla_result.idinstance`, `UNIQUE`), quindi un
 *   breach escalato resta **una** riga e **un** breach: il contratto non ha alcuna
 *   dimensione di escalation e la superficie non ne aggiunge;
 * - **`sample_size`** visibile per conformita', trend e drill-down (il `sample` del
 *   payload, con `limit`/`limited` espliciti: la conformita' non tronca, il trend
 *   ha il tetto di 400 bucket, il drill-down e' paginato);
 * - **filtro Cliente** (`customervalues`), offerto **solo** se il modello ha una
 *   variabile flaggata `isStatisticsKey`; altrimenti la superficie rende la guida
 *   del catalogo al posto del filtro, mai un filtro inerte;
 * - **target del dato visibile**: l'organizzazione di cui la metrica parla
 *   (`data-organization`) e il livello contato (processo/attivita'), dagli stessi
 *   filtri della richiesta.
 *
 * Cosa la superficie **non** fa, dichiarato e non simulato:
 * - **F4c**: insight AI, export PDF/Excel/email, preferenze individuali;
 * - **obiettivo/target di organizzazione** come valore (percentuale-obiettivo):
 *   il contratto P2 non ha alcun campo di target (`schemas/sla.py:102-181`) e il
 *   catalogo P1 nessuna chiave per un obiettivo; resta **residuo bloccante** —
 *   qui si rende il *target del dato* (organizzazione + livello), non un obiettivo;
 * - **modello e versione nel drill-down**: la chiave P1 del namespace drill-down
 *   per la versione del modello esiste ma e' **senza dato** nel contratto
 *   (`sla_result` non ha colonna di revisione modello), quindi **non e' resa**:
 *   dichiarata in `e2e/tests/sla/helpers/slaContract.ts`
 *   (`SLA_STATISTICS_UNUSED_CATALOG_KEYS`), non inventata. Non compare fra le
 *   chiavi di questo file proprio perche' la superficie non la usa: l'elenco qui
 *   sotto **e'** il manifest reso;
 * - **filtro Team** (`teamids`): fuori dal perimetro F4b e senza sorgente dei team
 *   nella pagina; non offerto.
 */

import dayjs, { Dayjs } from "dayjs";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import type {
  SlaComplianceLevel,
  SlaComplianceQuery,
  SlaComplianceResponse,
  SlaComplianceSample,
  SlaStatisticsGranularity,
} from "model/slaModel";

/**
 * Le due basi del contratto, nell'ordine in cui vanno rese. Non esiste un terzo
 * valore: la superficie non aggiunge una "base combinata".
 */
export const SLA_STATISTICS_BASES = ["original", "effective"] as const;

export type SlaStatisticsBasis = (typeof SLA_STATISTICS_BASES)[number];

/**
 * Chiavi usate dalla superficie Statistiche, tutte del catalogo P1
 * (`backend_statistiche/app/statics/sla_translations/manifest.json`): **32**
 * chiavi `sla.statistics.*` (20 di F4a piu' 12 di F4b: trend, drill-down,
 * `sample_size`, filtro Cliente, la sua guida e la label di stato di riga) piu'
 * le **esistenti riusate** (`SLA_STATISTICS_REUSED_KEYS`).
 * Raccolte qui e non sparse nei componenti perche' il piano sez. 4 le fa
 * concordare con Python P1 in un manifest: `getTranslation` restituisce la chiave
 * se il testo manca (`common/utilities.ts:904`), quindi questo elenco **e'** il
 * manifest lato Statistiche.
 *
 * Una chiave e' **mancante nel catalogo P1**: `excluded.authority_terminated`
 * (vedi `SLA_STATISTICS_MISSING_CATALOG_KEYS`): il payload P2 espone il terzo
 * esito escluso (`completion_kind = 4`) ma il catalogo P1 ha solo `cancelled` e
 * `interrupted`. Non si inventa un testo: la chiave e' derivata con la stessa
 * convenzione delle altre e resta da consegnare.
 */
export const SLA_STATISTICS_TEXT_KEYS = {
  /** Titolo della superficie (riusato anche come label del comando pagina). */
  title: "sla.statistics.title",
  /** Due compliance chiaramente etichettate (sez. 5 F4a). */
  complianceTitle: "sla.statistics.compliance.title",
  basisOriginal: "sla.statistics.compliance.original",
  basisEffective: "sla.statistics.compliance.effective",
  /** Percentuale; `rate_unavailable` e' l'N/D, mai 0%. */
  rate: "sla.statistics.compliance.rate",
  rateUnavailable: "sla.statistics.compliance.rate_unavailable",
  note: "sla.statistics.compliance.note",
  /** Denominatore esplicito accanto alla percentuale (sez. 5 F4a). */
  denominator: "sla.statistics.denominator",
  /** Copertura: i risultati con SLA sul totale; N/D a totale zero. */
  coverage: "sla.statistics.coverage",
  /** Tempo medio su base lavorativa (secondi persistiti, non giorni solari). */
  averageWorkingTime: "sla.statistics.average_working_time",
  /** Nessuna metrica per persona: la superficie lo dichiara con questa chiave. */
  privacy: "sla.statistics.privacy_note",
  /** Periodo `[from, to)` e timezone dichiarata (sez. 5 F4a). */
  period: "sla.statistics.period",
  periodTimezone: "sla.statistics.period.timezone",
  /** Periodo oltre il limite del contratto (399 giorni): dichiarato, non inviato. */
  periodLimit: "sla.statistics.period.limit",
  /** Filtro di livello: `level` e' obbligatorio nel contratto P2. */
  level: "sla.statistics.filter.level",
  /**
   * F4b — trend: titolo, granularita' giorno/settimana (due chiavi P1 **gia'
   * esistenti**, riusate anche dal payload come `period_day`/`period_week`).
   */
  trendTitle: "sla.statistics.trend.title",
  granularityDay: "sla.statistics.period.day",
  granularityWeek: "sla.statistics.period.week",
  /**
   * F4b — drill-down: titolo, titolo per livello (processo/passaggio), la nota
   * sui passaggi ripetuti e il comando "carica altri". Tutte chiavi P1 esistenti.
   */
  drilldownTitle: "sla.statistics.drilldown.title",
  drilldownByProcess: "sla.statistics.drilldown.by_process",
  drilldownByStep: "sla.statistics.drilldown.by_step",
  drilldownNoDoubleCount: "sla.statistics.drilldown.no_double_count",
  drilldownLoadMore: "sla.statistics.load_more",
  /** F4b — `sample_size` visibile accanto alla metrica (piano §5 F4b). */
  sampleSize: "sla.statistics.sample_size",
  /**
   * F4b — label dello stato di una riga di dettaglio. Il catalogo P1 ha la label
   * del **filtro** di stato (`sla.statistics.filter.state`) ma nessuna label per i
   * quattro `completion_kind`; la riga rende quindi il valore del contratto
   * accanto a questa label, senza inventare testi (vedi
   * `SLA_STATISTICS_NEEDED_CATALOG_KEYS` in `e2e/tests/sla/helpers/slaContract.ts`).
   */
  stateLabel: "sla.statistics.filter.state",
  /** F4b — filtro Cliente: presente solo con la variabile `isStatisticsKey`. */
  customerFilter: "sla.statistics.filter.customer",
  /** Guida resa **al posto** del filtro quando la variabile non e' configurata. */
  customerDisabled: "sla.statistics.filter.customer_disabled",
  /** Esiti esclusi, fuori dal denominatore (sez. 5 F4a). */
  excludedCancelled: "sla.statistics.excluded.cancelled",
  excludedInterrupted: "sla.statistics.excluded.interrupted",
  excludedAuthorityTerminated: "sla.statistics.excluded.authority_terminated",
  /** Senza SLA: fuori dal denominatore, reso a parte. */
  excludedNoSla: "sla.statistics.excluded.no_sla",
  noData: "sla.statistics.no_data",
} as const;

/**
 * Chiavi **gia' esistenti** riusate: nessuna di queste va duplicata in
 * `sla.statistics.*` (piano sez. 4, "non duplicare il catalogo").
 * - i due livelli del contratto sono le stesse grandezze del Monitor
 *   (`sla.monitor.level.*`: processo / attivita', il "task" del contratto P2);
 * - `as_of` e `stale` sono le stesse grandezze del Monitor: la freschezza di una
 *   metrica SLA non e' una nozione diversa e la soglia e' una sola
 *   (`slaMonitorModel.ts:355-368`);
 * - gli errori sono quelli del Monitor (`load_error`, `forbidden`): il contratto
 *   P2 risponde 403 `sla_forbidden` e 404 `sla_forbidden`
 *   (`services/sla/service_statistics.py:75,91`);
 * - `generic.loading`, le tre unita' di durata e `sla.editor.fixture_notice` sono
 *   i testi di servizio gia' usati dalle altre superfici.
 */
export const SLA_STATISTICS_REUSED_KEYS: string[] = [
  "generic.hours.caption",
  "generic.loading",
  "generic.minutes.caption",
  "generic.seconds.caption",
  "sla.editor.fixture_notice",
  SLA_MONITOR_TEXT_KEYS.asOf,
  SLA_MONITOR_TEXT_KEYS.forbidden,
  SLA_MONITOR_TEXT_KEYS.loadError,
  SLA_MONITOR_TEXT_KEYS.levelActivity,
  SLA_MONITOR_TEXT_KEYS.levelProcess,
  SLA_MONITOR_TEXT_KEYS.stale,
];

/**
 * Chiavi che il catalogo P1 **non** consegna e che il contratto P2 o la
 * superficie richiedono. Sono nominate, non inventate come testo.
 */
export const SLA_STATISTICS_MISSING_CATALOG_KEYS: string[] = [
  "sla.statistics.excluded.authority_terminated",
];

/**
 * Mappatura `message_keys` -> chiave di ripiego. Il contratto P2 **non manda
 * prosa**: espone le chiavi del catalogo P1 (`services/sla/statistics.py:92-108`,
 * `MESSAGE_KEYS`). La superficie usa la chiave del payload quando c'e' e ricade
 * su questa costante (stessi valori) quando manca, cosi' la label non dipende da
 * una risposta parziale.
 */
export const SLA_STATISTICS_MESSAGE_KEYS = {
  title: SLA_STATISTICS_TEXT_KEYS.complianceTitle,
  rate: SLA_STATISTICS_TEXT_KEYS.rate,
  rate_unavailable: SLA_STATISTICS_TEXT_KEYS.rateUnavailable,
  note: SLA_STATISTICS_TEXT_KEYS.note,
  coverage: SLA_STATISTICS_TEXT_KEYS.coverage,
  average_working_time: SLA_STATISTICS_TEXT_KEYS.averageWorkingTime,
  privacy: SLA_STATISTICS_TEXT_KEYS.privacy,
  period_timezone: SLA_STATISTICS_TEXT_KEYS.periodTimezone,
  period_limit: SLA_STATISTICS_TEXT_KEYS.periodLimit,
  /** F4b: campi che il payload P2 manda anche per trend e drill-down. */
  period_day: SLA_STATISTICS_TEXT_KEYS.granularityDay,
  period_week: SLA_STATISTICS_TEXT_KEYS.granularityWeek,
  drilldown_title: SLA_STATISTICS_TEXT_KEYS.drilldownTitle,
  drilldown_no_double_count: SLA_STATISTICS_TEXT_KEYS.drilldownNoDoubleCount,
  load_more: SLA_STATISTICS_TEXT_KEYS.drilldownLoadMore,
} as const;

export type SlaStatisticsMessageField = keyof typeof SLA_STATISTICS_MESSAGE_KEYS;

/**
 * Chiave da rendere per un campo: quella dichiarata dal payload P2 se presente,
 * altrimenti la costante del modello. Mai una stringa costruita a mano.
 */
export const slaStatisticsMessageKey = (
  response: Pick<SlaComplianceResponse, "message_keys"> | null | undefined,
  field: SlaStatisticsMessageField,
): string => {
  const declared = response?.message_keys?.[field];
  return typeof declared === "string" && declared.length > 0
    ? declared
    : SLA_STATISTICS_MESSAGE_KEYS[field];
};

// ─── Livelli (obbligatori nel contratto) ─────────────────────────────────────

/** I due livelli del contratto P2: nessun aggregato "tutti" (`schemas/sla.py:26`). */
export const SLA_STATISTICS_LEVELS: readonly SlaComplianceLevel[] = [
  "process",
  "task",
];

export const SLA_STATISTICS_LEVEL_DEFAULT: SlaComplianceLevel = "process";

/** Chiave della label di un livello: riusa le due del Monitor, non ne crea. */
export const slaStatisticsLevelKey = (level: SlaComplianceLevel): string =>
  level === "task"
    ? SLA_MONITOR_TEXT_KEYS.levelActivity
    : SLA_MONITOR_TEXT_KEYS.levelProcess;

// ─── Periodo [from, to) ──────────────────────────────────────────────────────

/** Limite del contratto: 399 giorni (`services/sla/statistics.py:85`). */
export const SLA_STATISTICS_MAX_PERIOD_DAYS = 399;

export interface SlaStatisticsRange {
  /** Data ISO del primo giorno **incluso**. */
  from: string;
  /** Data ISO del primo giorno **escluso** (periodo `[from, to)`). */
  to: string;
}

/**
 * Periodo SLA dall'intervallo della pagina Statistiche.
 *
 * L'intervallo della pagina e' un intervallo di **istanti** con estremo incluso
 * (`DateRangePickerMenu` produce `startOf("day")`/`endOf("day")`), mentre il
 * contratto P2 vuole date con periodo `[from, to)` a mezzanotte locale
 * (`statistics.py:152-171`). La conversione e' quindi **dichiarata**: primo
 * giorno = giorno di `start`, ultimo giorno escluso = giorno di `end` + 1, cosi'
 * l'intero ultimo giorno scelto resta dentro il periodo. Il periodo **reso** e'
 * questo (non un valore diverso): le asserzioni confrontano cio' che la
 * superficie mostra con cio' che manda.
 *
 * Nessun calcolo di calendario e nessuna conversione di timezone nel browser: le
 * date sono giorni locali e la timezone la risolve il Server.
 */
export const slaStatisticsPeriod = (
  interval: { start: Dayjs | null; end: Dayjs | null },
  now: Dayjs = dayjs(),
): SlaStatisticsRange => {
  const start =
    interval.start && dayjs(interval.start).isValid() ? dayjs(interval.start) : now;
  const end =
    interval.end && dayjs(interval.end).isValid() ? dayjs(interval.end) : start;
  return {
    from: start.startOf("day").format("YYYY-MM-DD"),
    to: end.startOf("day").add(1, "day").format("YYYY-MM-DD"),
  };
};

/** Giorni del periodo, stesso conteggio del Server (`SlaPeriod.days`). */
export const slaStatisticsPeriodDays = (range: SlaStatisticsRange): number =>
  dayjs(range.to).diff(dayjs(range.from), "day");

/**
 * Il periodo sta nel limite del contratto? Oltre il limite il Server risponde 422
 * `sla_calendar_unavailable` (`controllers/sla.py:103-113`): la superficie lo
 * **dichiara** invece di inviare una richiesta che sa gia' non valida.
 */
export const slaStatisticsPeriodWithinLimit = (
  range: SlaStatisticsRange,
  maxDays: number = SLA_STATISTICS_MAX_PERIOD_DAYS,
): boolean => {
  const days = slaStatisticsPeriodDays(range);
  return days > 0 && days <= maxDays;
};

// ─── Query ───────────────────────────────────────────────────────────────────

/**
 * Query della conformita' dai filtri della pagina. `level` e' **sempre** inviato
 * (obbligatorio nel contratto); `modelids` solo se la pagina ha un modello
 * selezionato. Cliente (`customervalues`) e team (`teamids`) sono F4b: non si
 * mandano e non si offrono.
 */
export const slaStatisticsQuery = (
  level: SlaComplianceLevel,
  range: SlaStatisticsRange,
  modelId?: number | null,
): SlaComplianceQuery => {
  const query: SlaComplianceQuery = {
    from: range.from,
    to: range.to,
    level,
  };
  if (typeof modelId === "number" && Number.isFinite(modelId)) {
    query.modelids = String(modelId);
  }
  return query;
};

// ─── F4b: trend, drill-down, campione e Cliente ──────────────────────────────

/**
 * **F4b** — trend, drill-down, `sample_size` e filtro Cliente (piano §5 F4b,
 * §8 riga 128). Tutto cio' che sta qui e' contratto reale del backend P2
 * (`backend_statistiche/app/schemas/sla.py:117-181`,
 * `app/controllers/sla.py:190-278`) e non una forma inventata.
 */

/** Granularita' del trend: due soli valori, `day` e `week` (`schemas/sla.py:27`). */
export const SLA_STATISTICS_GRANULARITIES: readonly SlaStatisticsGranularity[] = [
  "day",
  "week",
];

export const SLA_STATISTICS_GRANULARITY_DEFAULT: SlaStatisticsGranularity = "day";

/**
 * Chiave della label di granularita': riusa le due chiavi P1 del periodo
 * (`sla.statistics.period.day|week`), che sono **le stesse** che il payload P2
 * manda come `period_day`/`period_week`. Non si creano chiavi nuove.
 */
export const slaStatisticsGranularityKey = (
  granularity: SlaStatisticsGranularity,
): string =>
  granularity === "week"
    ? SLA_STATISTICS_TEXT_KEYS.granularityWeek
    : SLA_STATISTICS_TEXT_KEYS.granularityDay;

/** Tetto dei bucket del trend: 400 (`statistics.py:88`, `MAX_TREND_BUCKETS`). */
export const SLA_STATISTICS_MAX_TREND_BUCKETS = 400;

/** Paginazione del drill-down: default 50, tetto 500 (`schemas/sla.py:53`, `:235`). */
export const SLA_STATISTICS_DRILLDOWN_PAGE_SIZE = 50;
export const SLA_STATISTICS_MAX_DRILLDOWN_PAGE_SIZE = 500;

/**
 * Rotta del drill-down: il livello **e' nel path**, non nella query
 * (`controllers/sla.py:225-278`). Il contratto P2 chiama "step" l'attivita':
 * e' lo stesso livello che la conformita' chiama `task`.
 */
export const slaStatisticsDrilldownPath = (level: SlaComplianceLevel): string =>
  level === "task" ? "drilldown/step" : "drilldown/process";

/** Titolo del drill-down per livello: due chiavi P1 distinte, non un testo unico. */
export const slaStatisticsDrilldownTitleKey = (level: SlaComplianceLevel): string =>
  level === "task"
    ? SLA_STATISTICS_TEXT_KEYS.drilldownByStep
    : SLA_STATISTICS_TEXT_KEYS.drilldownByProcess;

/**
 * Periodo di un **bucket** del trend, per il drill-down dal punto aggregato.
 *
 * Il bucket arriva dal Server come data **locale** gia' risolta con la timezone
 * organizzativa (`queries.py:73-79`) e il contratto vuole un periodo `[from, to)`
 * di date locali. Qui si somma una giornata (granularita' `day`) o una settimana
 * (granularita' `week`, il bucket e' gia' il lunedi' di `date_trunc('week', ...)`)
 * con la stessa semantica di `slaStatisticsPeriod`: e' aritmetica di date su un
 * valore del Server, **non** un calcolo del calendario lavorativo nel browser
 * (nessuna chiusura, nessun fuso, nessuna festivita').
 */
export const slaStatisticsBucketRange = (
  bucket: string,
  granularity: SlaStatisticsGranularity,
): SlaStatisticsRange => {
  const start = dayjs(bucket).startOf("day");
  const next =
    granularity === "week" ? start.add(1, "week") : start.add(1, "day");
  return {
    from: start.format("YYYY-MM-DD"),
    to: next.format("YYYY-MM-DD"),
  };
};

/**
 * Query del **trend**: gli stessi filtri della conformita' piu' `granularity`
 * (`schemas/sla.py:42-64`). Nessuna base in piu': il trend porta **entrambe** le
 * basi, quindi non esiste una "base del trend" diversa da quella della vista.
 */
export const slaStatisticsTrendQuery = (
  level: SlaComplianceLevel,
  range: SlaStatisticsRange,
  granularity: SlaStatisticsGranularity,
  modelId?: number | null,
  customerValues?: string,
): SlaComplianceQuery => ({
  ...slaStatisticsQuery(level, range, modelId),
  granularity,
  ...(customerValues ? { customervalues: customerValues } : {}),
});

/**
 * Query del **drill-down**: gli stessi filtri (senza `level`, che sta nel path),
 * piu' la paginazione (`schemas/sla.py:42-64`). Riceve lo **stesso** periodo del
 * trend/conformita' quando si apre dal periodo intero.
 */
export const slaStatisticsDrilldownQuery = (
  range: SlaStatisticsRange,
  page: number,
  modelId?: number | null,
  customerValues?: string,
): SlaComplianceQuery => {
  const query: SlaComplianceQuery = {
    from: range.from,
    to: range.to,
    page: page > 0 ? page : 1,
    page_size: SLA_STATISTICS_DRILLDOWN_PAGE_SIZE,
  };
  if (typeof modelId === "number" && Number.isFinite(modelId)) {
    query.modelids = String(modelId);
  }
  if (customerValues) {
    query.customervalues = customerValues;
  }
  return query;
};

/**
 * Valori del filtro Cliente dal testo inserito: il contratto li vuole come
 * **stringa separata da virgole** (`controllers/sla.py:44-59`, `parse_list_param`).
 * Nessun valore viene dedotto: lista vuota = filtro non inviato.
 */
export const slaStatisticsCustomerValues = (raw: string): string | undefined => {
  const values = raw
    .split(",")
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  return values.length > 0 ? values.join(",") : undefined;
};

/** Nome della variabile-chiave delle statistiche nel `userschema` del modello. */
export const SLA_STATISTICS_KEY_FLAG = "isStatisticsKey";

/**
 * Il modello ha una variabile flaggata `isStatisticsKey`?
 *
 * E' la **condizione del filtro Cliente** (piano §5 F4b, §8.6 della spec UI): il
 * flag vive nella definizione dei campi custom del modello
 * (`Flowenti-Server/plans/SLA_Implementation_Plan.md:208-211`, che lo dichiara su
 * `processmodels.userschema`) e il valore storico del campo e' congelato alla
 * chiusura (`20260916_sla_core.sql:237-239`).
 *
 * Lettura **difensiva**: il `userschema` e' JSON di definizioni campo
 * (`{id, name, type, inCard}` in `ProcessModelSchema.tsx:22-27`) e il flag e' un
 * campo in piu'. Nessuna deduzione da nome o tipo del campo: solo il flag
 * esplicito `isStatisticsKey === true`. Con input illeggibile o assente la
 * risposta e' `false`: **senza prova, il filtro non si offre** (mai un filtro
 * inerte, e nessun valore dedotto da un nome arbitrario — `queries.py:100-106`).
 */
export const slaStatisticsHasStatisticsKey = (userschema: unknown): boolean => {
  let fields: unknown = userschema;
  if (typeof userschema === "string") {
    try {
      fields = JSON.parse(userschema);
    } catch {
      return false;
    }
  }
  if (!Array.isArray(fields)) return false;
  return fields.some(
    (field) =>
      typeof field === "object" &&
      field !== null &&
      (field as Record<string, unknown>)[SLA_STATISTICS_KEY_FLAG] === true,
  );
};

/**
 * Dimensione del campione resa: `sample` del payload P2 (contratto reale).
 * `limit` e `limited` sono **espliciti** perche' le tre superfici troncano in
 * modi diversi: la conformita' non tronca (`limit: null`), il trend ha il tetto
 * di 400 bucket, il drill-down e' paginato. Nessun valore viene ricalcolato:
 * si rende `rows`, il tetto (se c'e') e il fatto che i dati siano limitati.
 */
export interface SlaStatisticsSampleView {
  available: boolean;
  rows: number | null;
  limit: number | null;
  limited: boolean;
  /** Testo del campione senza unita' di misura inventate (solo numeri/dividi). */
  text: string;
}

export const slaStatisticsSample = (
  sample: SlaComplianceSample | null | undefined,
): SlaStatisticsSampleView => {
  if (!sample || !Number.isFinite(sample.rows)) {
    return { available: false, rows: null, limit: null, limited: false, text: "" };
  }
  const rows = Number(sample.rows);
  const limit =
    typeof sample.limit === "number" && Number.isFinite(sample.limit)
      ? sample.limit
      : null;
  return {
    available: true,
    rows,
    limit,
    limited: sample.limited === true,
    text: limit === null ? String(rows) : `${rows} / ${limit}`,
  };
};

/**
 * Identita' resa per una riga di drill-down: `sla_instance_id`, cioe'
 * `sla_result.idinstance` (`UNIQUE`, `20260916_sla_core.sql:221-222`). E' **la**
 * ragione per cui un breach escalato resta un solo breach: la riga e' il
 * risultato finale di quell'istanza, non un evento per livello. La superficie non
 * aggiunge alcuna dimensione di escalation e nessuna riga viene duplicata.
 */
export const SLA_STATISTICS_ROW_IDENTITY = "sla_instance_id" as const;

// ─── Percentuali e N/D ───────────────────────────────────────────────────────

export interface SlaStatisticsRate {
  /** false = N/D: la superficie rende la chiave "non disponibile", mai 0%. */
  available: boolean;
  text: string;
}

/**
 * Percentuale localizzata, o N/D. Il contratto garantisce
 * `percent = null` **sse** `eligible = 0` (`schemas/sla.py:74-82`): la superficie
 * applica la stessa regola in lettura difensiva e **non** trasforma mai l'assenza
 * in `0%`, ne' calcola una percentuale che il Server non ha mandato.
 */
export const slaStatisticsRate = (
  value: number | null | undefined,
  eligible: number,
  locale: string,
  unavailableText: string,
): SlaStatisticsRate => {
  if (
    value === null ||
    value === undefined ||
    !Number.isFinite(value) ||
    !Number.isFinite(eligible) ||
    eligible <= 0
  ) {
    return { available: false, text: unavailableText };
  }
  return {
    available: true,
    text: new Intl.NumberFormat(locale, {
      style: "percent",
      maximumFractionDigits: 2,
    }).format(value / 100),
  };
};

/**
 * Copertura/tempo medio: stessa regola dell'N/D (il contratto li manda `null` a
 * totale o denominatore zero: `statistics.py:221-235`).
 */
export const slaStatisticsNullableRate = (
  value: number | null | undefined,
  locale: string,
  unavailableText: string,
): SlaStatisticsRate => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return { available: false, text: unavailableText };
  }
  return {
    available: true,
    text: new Intl.NumberFormat(locale, {
      style: "percent",
      maximumFractionDigits: 2,
    }).format(value / 100),
  };
};

export type SlaStatisticsDuration =
  | { available: false; text: string }
  | { available: true; value: string; unitKey: string };

/**
 * Tempo medio su base **lavorativa**: `average_working_seconds` sono secondi
 * lavorativi persistiti dal Server (`statistics.py:231-235`), quindi si
 * convertono di unita' — nessun giorno solare, nessun `86400` fisso (sez. 4).
 * `unitKey` e' una delle chiavi `generic.*.caption` gia' esistenti, come fa il
 * Monitor (`slaMonitorWorkingDuration`): la label non e' mai una stringa di
 * unita' scritta nel componente.
 */
export const slaStatisticsWorkingDuration = (
  seconds: number | null | undefined,
  locale: string,
  unavailableText: string,
): SlaStatisticsDuration => {
  if (
    seconds === null ||
    seconds === undefined ||
    !Number.isFinite(seconds) ||
    seconds < 0
  ) {
    return { available: false, text: unavailableText };
  }
  const format = (amount: number): string =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(amount);
  if (seconds >= 3600) {
    return { available: true, value: format(seconds / 3600), unitKey: "generic.hours.caption" };
  }
  if (seconds >= 60) {
    return {
      available: true,
      value: format(Math.round(seconds / 60)),
      unitKey: "generic.minutes.caption",
    };
  }
  return { available: true, value: format(seconds), unitKey: "generic.seconds.caption" };
};

/**
 * Durata lavorativa leggibile per schede e tabelle ("52 min", "1 h 5 min"):
 * stessi secondi lavorativi del Server, convertiti solo in ore e minuti (mai in
 * giorni: un giorno lavorativo non e' 86400 secondi). Le unita' vengono da
 * `Intl.NumberFormat` (`style: "unit"`), quindi sono localizzate senza chiavi.
 * `null` = N/D (secondi assenti o non validi): chi chiama rende il testo N/D.
 */
export const slaStatisticsFormatWorkingSeconds = (
  seconds: number | null | undefined,
  locale: string,
): string | null => {
  if (
    seconds === null ||
    seconds === undefined ||
    !Number.isFinite(seconds) ||
    seconds < 0
  ) {
    return null;
  }
  const unit = (amount: number, name: "hour" | "minute" | "second"): string => {
    try {
      return new Intl.NumberFormat(locale, {
        style: "unit",
        unit: name,
        unitDisplay: "short",
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${amount} ${name === "hour" ? "h" : name === "minute" ? "min" : "s"}`;
    }
  };
  if (seconds < 60) return unit(Math.round(seconds), "second");
  let hours = Math.floor(seconds / 3600);
  let minutes = Math.round((seconds % 3600) / 60);
  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }
  if (hours === 0) return unit(minutes, "minute");
  return minutes === 0 ? unit(hours, "hour") : `${unit(hours, "hour")} ${unit(minutes, "minute")}`;
};

// ─── Esiti esclusi e senza SLA (fuori dal denominatore) ──────────────────────

export interface SlaStatisticsOutcome {
  field: "cancelled" | "interrupted" | "authority_terminated";
  /** `completion_kind` del DDL Server (`statistics.py:78-82`). */
  completionKind: 2 | 3 | 4;
  key: string;
}

/**
 * I tre esiti **esclusi** dal denominatore, con il `completion_kind` che li
 * rappresenta. Solo `COMPLETION_COMPLETED = 1` entra in `eligible`: un processo
 * annullato, interrotto o chiuso d'autorita' **non** e' un completamento valido e
 * non compare nel denominatore della compliance (`queries.py:55-73`).
 */
export const SLA_STATISTICS_EXCLUDED_OUTCOMES: readonly SlaStatisticsOutcome[] = [
  {
    field: "cancelled",
    completionKind: 2,
    key: SLA_STATISTICS_TEXT_KEYS.excludedCancelled,
  },
  {
    field: "interrupted",
    completionKind: 3,
    key: SLA_STATISTICS_TEXT_KEYS.excludedInterrupted,
  },
  {
    field: "authority_terminated",
    completionKind: 4,
    key: SLA_STATISTICS_TEXT_KEYS.excludedAuthorityTerminated,
  },
];

/**
 * Sorgente della dimensione resa: la superficie non espone utente, ruolo o
 * livello di escalation. Il contratto P2 non porta metriche nominative
 * (`statistics.py:334-339`), le metriche di escalation sono escluse per decisione
 * 8 (`statistics.py:26-55`) e la stessa regola vale per trend e drill-down:
 * `SlaTrendPoint` e `SlaDrilldownRow` (`schemas/sla.py:117-162`) non hanno alcuna
 * colonna di persona o di livello raggiunto, quindi il drill-down non puo'
 * **duplicare** un breach per livello: non esiste la dimensione con cui farlo.
 */
export const SLA_STATISTICS_DIMENSION = "no_user_or_role_dimension" as const;

/**
 * Campi del payload di conformita' P2, **esattamente** gli alias dello schema
 * (`schemas/sla.py:102-114`). E' la verifica "nessun campo inventato": il DTO
 * client (`SlaComplianceResponse`) deve combaciare con questa lista.
 */
export const SLA_STATISTICS_COMPLIANCE_PAYLOAD_FIELDS: string[] = [
  "as_of",
  "average_working_seconds",
  "coverage_percent",
  "effective",
  "excluded",
  "level",
  "message_keys",
  "original",
  "period",
  "sample",
  "timezone",
  "without_sla",
];

/**
 * Campi che **non** devono comparire nel DTO di conformita': un payload di
 * conformita' per-persona sarebbe una metrica nominativa, esclusa da P2
 * (`services/sla/statistics.py:334-339`) e vietata dal criterio F4a ("nessuna
 * metrica per persona"). Verificato come assenza.
 */
export const SLA_STATISTICS_FORBIDDEN_DTO_FIELDS: string[] = [
  "assignee",
  "escalation_level",
  "iduser",
  "per_user",
  "responsible_role_id",
  "responsible_user_id",
  "user_id",
  "userfullname",
];

// ─── Freschezza (sez. 5 F3d, condivisa col Monitor) ──────────────────────────

/**
 * Soglia di freschezza: **una sola** per tutte le superfici SLA. Vive in
 * `slaMonitorModel.ts:368` (`SLA_MONITOR_STALE_AFTER_SECONDS`) ed e' riusata qui
 * perche' il contratto non porta un limite di validita' e la grandezza e' la
 * stessa: duplicarla creerebbe due verita' sulla stessa domanda.
 */
export const SLA_STATISTICS_STALE_SOURCE = "sla_monitor_stale_after_seconds" as const;
